/**
 * Unified target read/write - Rig State Milestone 2 (2026-08-15, PLAN.md
 * QUEUED "Rig States" > "v1 build"). Bridges the two live-value sources
 * that exist so far - `liveState/registry.ts`'s 4 named signals
 * (transpose/capo/autotune key+scale) and `liveState/sendCache.ts`'s
 * generic MIDI CC/note cache - behind one read/write pair, so a Rig State
 * look's `targetId` list doesn't need to know or care which source a given
 * target actually comes from.
 */

import { getLiveTarget } from './registry.ts';
import { readSendCache, DEFAULT_PORT_TOKEN } from './sendCache.ts';
import { fireAction } from '../opbridge/dispatch.ts';
import { setConcertTranspose, transposeForCapo } from '../transpose/session.ts';
import { setAutotuneKey, setAutotuneScale, DEFAULT_AUTOTUNE_CHANNEL, type AutotuneScale } from '../autotune/session.ts';

export interface TargetReading {
  value: number | string;
  display: string;
}

/** Read a target's current value - the registry first (always-known,
 *  narrow domain), falling back to the generic send-cache (arbitrary MIDI
 *  CC/note). Returns null when nothing is known yet - "no value known,"
 *  never a guess, per the locked capture semantics. */
export function readTarget(targetId: string): TargetReading | null {
  const registered = getLiveTarget(targetId);
  if (registered) {
    const reading = registered.read();
    return reading ? { value: reading.value, display: reading.display } : null;
  }
  const cached = readSendCache(targetId);
  if (!cached) return null;
  const raw = cached.raw ?? Math.round(cached.value * 127);
  return { value: raw, display: String(raw) };
}

export function parseMidiTargetId(targetId: string): { port: string; kind: 'cc' | 'note'; channel: number; number: number } | null {
  const parts = targetId.split(':');
  if (parts.length !== 5 || parts[0] !== 'midi') return null;
  const port = parts[1] ?? '';
  const kind = parts[2];
  const channel = Number(parts[3]);
  const num = Number(parts[4]);
  if (!port || (kind !== 'cc' && kind !== 'note')) return null;
  if (!Number.isFinite(channel) || !Number.isFinite(num)) return null;
  return { port, kind, channel, number: num };
}

export interface WriteOutcome {
  ok: boolean;
  detail?: string;
}

/** Write a value to a target - the registry's own setter for the 4 named
 *  targets (transpose/capo/autotune key+scale), or a generic MIDI CC/note
 *  send via `fireAction` for anything else. `bridgeTarget` follows
 *  `fireAction`'s own convention (empty = default bridge).
 *
 *  Known v1 simplification: `autotuneKey`/`autotuneScale` need a MIDI
 *  channel to dispatch on, but the channel is authored per-item
 *  (`WavesAutotuneItem.channel`), not global like the key/scale value
 *  itself. Recall uses `DEFAULT_AUTOTUNE_CHANNEL` rather than resolving a
 *  specific item's channel - correct for the common single-channel case,
 *  a known gap for a multi-channel autotune setup. Flagged, not solved. */
export async function writeTarget(targetId: string, value: number | string, bridgeTarget = ''): Promise<WriteOutcome> {
  if (targetId === 'transpose' && typeof value === 'number') {
    return setConcertTranspose(value, bridgeTarget ? [bridgeTarget] : undefined);
  }
  if (targetId === 'capo' && typeof value === 'number') {
    return setConcertTranspose(transposeForCapo(value), bridgeTarget ? [bridgeTarget] : undefined);
  }
  // The real outcome, not a blanket ok: a recall that reached nothing read "4/4" (2026-11-03 audit G-2).
  if (targetId === 'autotuneKey' && typeof value === 'number') {
    return setAutotuneKey(value, DEFAULT_AUTOTUNE_CHANNEL, bridgeTarget ? [bridgeTarget] : undefined);
  }
  if (targetId === 'autotuneScale' && typeof value === 'string') {
    return setAutotuneScale(value as AutotuneScale, DEFAULT_AUTOTUNE_CHANNEL, bridgeTarget ? [bridgeTarget] : undefined);
  }
  const parsed = parseMidiTargetId(targetId);
  if (!parsed || typeof value !== 'number') {
    return { ok: false, detail: `unrecognized targetId "${targetId}"` };
  }
  // The default-output token names no port: the bridge's own default resolves it.
  const port = parsed.port === DEFAULT_PORT_TOKEN ? {} : { port: parsed.port };
  const results = parsed.kind === 'cc'
    ? await fireAction({ id: 'rigstate-recall', target: bridgeTarget, type: 'midi.cc', ...port, channel: parsed.channel, cc: parsed.number, value })
    // duration: 0 - a recall SETS the value, it doesn't schedule a note-off
    // (sendMidiNote only schedules one when durationMs > 0).
    : await fireAction({ id: 'rigstate-recall', target: bridgeTarget, type: 'midi.note', ...port, channel: parsed.channel, note: parsed.number, velocity: value, duration: 0 });
  const result = results[0];
  if (result?.kind === 'ok') return { ok: true };
  const detail = result?.detail ?? result?.kind;
  return detail !== undefined ? { ok: false, detail } : { ok: false };
}
